import { getStationCode } from "../../network/NetworkUtil";
import { getAPIGatewayDataFromParent, getCycleDataFromParent } from "../../handler/ParentDataHandler";
import { logOperationalMetric } from "../../util/Util";
import { INBOUND_ACTION_TYPES, INBOUND_STRINGS, STOW_WIP_LOWER, STOW_WIP_UPPER, WIP_TARGET } from "../../util/Constants";

const metricProcessName = "SCC_INBOUND_STOW";
const CYCLE_1_ID = "CYCLE_1";

/**
 * Resolves CYCLE_1 data from the parent app.
 * Returns { cycleName, cycleId } for CYCLE_1, or null if unavailable.
 */
async function getCycle1Data() {
    try {
        const cyclesResponse = await getCycleDataFromParent();
        if (!cyclesResponse || !cyclesResponse.cycleList) {
            return null;
        }
        const allCycles = JSON.parse(cyclesResponse.cycleList)
            .filter(function(cycle) { return cycle.cycleId !== "AllCycles"; });

        const cycle1 = allCycles.find(function(cycle) {
            return cycle.cycleId === CYCLE_1_ID;
        });

        if (!cycle1) {
            return null;
        }

        return {
            cycleName: cycle1.cycleId
        };
    } catch (error) {
        console.warn("[getCycle1Data] Failed to get cycle data:", error);
        return null;
    }
}

/**
 * Builds a metric request for the SVS GetMetricData API.
 */
function buildStowMetricRequest(metricNames, cycleId) {
    return {
        resourcePath: "/svs/getMetricData",
        httpMethod: "post",
        processName: "stow",
        requestBody: {
            metricDataQueries: metricNames.map(function(metricName) {
                return {
                    metric: {
                        metricName: metricName,
                        dimensions: [
                            { name: "STATION_CODE", value: getStationCode() },
                            { name: "CYCLE_ID", value: cycleId }
                        ]
                    }
                };
            })
        }
    };
}

/**
 * Fetch Stow FPH target via IVS getPerformanceMetric API.
 */
async function fetchStowFPHTarget(cycleName) {
    var request = {
        resourcePath: "/ivs/getPerformanceMetric",
        httpMethod: "post",
        processName: "induct",
        requestBody: {
            nodeId: getStationCode(),
            filterMap: {
                Cycle: [cycleName]
            },
            performanceMetricsList: ["TARGET_SORT_FPH"]
        }
    };

    try {
        var response = await getAPIGatewayDataFromParent(request);
        if (response && response.status === 200 && response.data) {
            var output = response.data;
            if (output.groupPerformanceMetrics && output.groupPerformanceMetrics.TARGET_SORT_FPH !== undefined) {
                return output.groupPerformanceMetrics.TARGET_SORT_FPH;
            }
        }
    } catch (error) {
        console.warn("[fetchStowFPHTarget] Failed:", error);
    }
    return 0;
}

/**
 * Fetch Stow FPH actual via SVS GetPackageMetrics API.
 * filters: { NODE, INDUCTED_CYCLE }, metrics: ["STOW_RATE"], groupBy: "NONE"
 */
async function fetchStowFPH(nodeId, cycle) {
    var request = {
        resourcePath: "/svs/packages/metrics",
        httpMethod: "post",
        processName: "stow",
        requestBody: {
            groupBy: "NONE",
            filters: {
                NODE: [nodeId],
                INDUCTED_CYCLE: [cycle.cycleName]
            },
            metrics: ["STOW_RATE"],
            isAggregationRequired: false
        }
    };

    try {
        var response = await getAPIGatewayDataFromParent(request);
        if (response && response.status === 200 && response.data) {
            var grouped = response.data.groupedPackageMetrics;
            if (grouped && grouped.Total && grouped.Total.stowRate !== undefined) {
                return grouped.Total.stowRate;
            }
        }
    } catch (error) {
        console.warn("[fetchStowFPH] GetPackageMetrics failed:", error);
    }
    return null;
}

/**
 * Fetch Stow WIP and Stow WIP Compliance via SVS GetMetricData API.
 */
async function fetchStowWIPAndCompliance(cycleId) {
    const request = buildStowMetricRequest(
        ["STOW_WIP", "STOW_WIP_COMPLIANCE_DATA"],
        cycleId
    );

    var result = {
        stowWip: null,
        stowWipLower: null,
        stowWipUpper: null,
        stowWipCompliance: null,
        stowWipComplianceTarget: null
    };

    try {
        const response = await getAPIGatewayDataFromParent(request);

    if (response && response.status === 200 && response.data && response.data.metricResults) {
        response.data.metricResults.forEach(function(metricData) {
            if (metricData.metric.metricName === "STOW_WIP") {
                var wipValue = metricData.metricValues.find(function(mv) {
                    return mv.name === "STOW_WIP";
                });
                var lowerTarget = metricData.metricValues.find(function(mv) {
                    return mv.name === "STOW_WIP_LOWER_TARGET";
                });
                var upperTarget = metricData.metricValues.find(function(mv) {
                    return mv.name === "STOW_WIP_UPPER_TARGET";
                });
                result.stowWip = wipValue ? wipValue.value : null;
                result.stowWipLower = lowerTarget ? lowerTarget.value : STOW_WIP_LOWER;
                result.stowWipUpper = upperTarget ? upperTarget.value : STOW_WIP_UPPER;
            }
            if (metricData.metric.metricName === "STOW_WIP_COMPLIANCE_DATA") {
                var complianceValue = metricData.metricValues.find(function(mv) {
                    return mv.name === "STOW_WIP";
                });
                var complianceTarget = metricData.metricValues.find(function(mv) {
                    return mv.name === "STOW_WIP_COMPLIANCE_TARGET";
                });
                result.stowWipCompliance = complianceValue ? complianceValue.value : null;
                result.stowWipComplianceTarget = complianceTarget ? complianceTarget.value : WIP_TARGET;
            }
        });
    }
    } catch (error) {
        console.warn("[fetchStowWIPAndCompliance] Failed:", error);
    }

    return result;
}

/**
 * Main entry point: fetch all 3 stow metrics and dispatch results.
 * Called from the InboundDataReducer on LOAD_INBOUND_DATA.
 */
export async function fetchStowMetrics(dispatcher) {
    var startTime = Date.now();
    var nodeId = getStationCode();

    if (!nodeId) {
        dispatcher({
            type: INBOUND_ACTION_TYPES.STOW_METRICS_FAILURE,
            error: "No station code available"
        });
        return;
    }

    try {
        var cycle = await getCycle1Data();
        if (!cycle) {
            dispatcher({
                type: INBOUND_ACTION_TYPES.STOW_METRICS_FAILURE,
                error: "No cycle data available"
            });
            return;
        }

        // Fetch all in parallel — use cycleName (e.g. "CYCLE_1") as the CYCLE_ID dimension.
        var results = await Promise.all([
            fetchStowFPH(nodeId, cycle),
            fetchStowWIPAndCompliance(cycle.cycleName),
            fetchStowFPHTarget(cycle.cycleName)
        ]);

        var stowFph = results[0];
        var wipData = results[1];
        var stowFphTarget = results[2];

        if (stowFph === null && wipData.stowWip === null) {
            logOperationalMetric(startTime, metricProcessName, "fetchStowMetrics", false);
            dispatcher({
                type: INBOUND_ACTION_TYPES.STOW_METRICS_FAILURE,
                error: INBOUND_STRINGS.ERROR_FETCH_STOW_METRICS
            });
            return;
        }

        logOperationalMetric(startTime, metricProcessName, "fetchStowMetrics", true);
        dispatcher({
            type: INBOUND_ACTION_TYPES.RECEIVE_STOW_METRICS,
            data: {
                stowFph: stowFph != null ? stowFph : 0,
                stowFphTarget: stowFphTarget != null ? stowFphTarget : 0,
                stowWip: wipData.stowWip != null ? wipData.stowWip : 0,
                stowWipLower: wipData.stowWipLower != null ? wipData.stowWipLower : STOW_WIP_LOWER,
                stowWipUpper: wipData.stowWipUpper != null ? wipData.stowWipUpper : STOW_WIP_UPPER,
                stowWipCompliance: wipData.stowWipCompliance != null ? wipData.stowWipCompliance : 0,
                stowWipComplianceTarget: wipData.stowWipComplianceTarget != null ? wipData.stowWipComplianceTarget : WIP_TARGET
            }
        });
    } catch (error) {
        logOperationalMetric(startTime, metricProcessName, "fetchStowMetrics", false);
        dispatcher({
            type: INBOUND_ACTION_TYPES.STOW_METRICS_FAILURE,
            error: error.message || INBOUND_STRINGS.ERROR_FETCH_STOW_METRICS
        });
    }
}



// WEBPACK FOOTER //
// ./src/components/inbound/network/StowMetricsAPI.js